import Seo from "../../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand, EASE } from "../../components/Shared";
import { motion } from "framer-motion";
import { CERAMIC, BUSINESS } from "../../data/site";
import { Check, Ship, Plane, Waves } from "lucide-react";
import { Link } from "react-router-dom";

const phantom = CERAMIC.find((c) => c.name === "System X Phantom");

export default function MarineAviation() {
  return (
    <>
      <Seo
        title="Marine & Aviation Ceramic Coating in Sioux Falls, SD | System X Phantom — A+ Detailing"
        description="System X Phantom ceramic coating for boats, watercraft and aircraft — the industry's first professional two-part coating with a lifetime warranty. Free quotes from A+ Detailing, Sioux Falls SD."
        path="/marine-aviation-ceramic"
      />
      <PageHero
        eyebrow="Marine & Aviation Ceramic Coating"
        lines={["BOATS.", "PLANES.", "PROTECTED."]}
        lead="System X Phantom is the coating rated for marine and aviation surfaces — the industry's first professional two-part ceramic, built with a dramatically stronger cross-linked matrix for life on the water and in the air."
        chips={["System X Phantom", "Lifetime warranty", "Free quote"]}
        testPrefix="marine"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-5 lg:grid-cols-2">
          {[
            {
              icon: Ship,
              t: "Marine",
              d: "Gel coat, fiberglass and painted surfaces on boats and watercraft — Phantom's chemical resistance and corrosion protection stand up to water, sun and salt exposure.",
            },
            {
              icon: Plane,
              t: "Aviation",
              d: "Aircraft exterior surfaces benefit from Phantom's long-lasting gloss, UV protection and slick, easy-clean finish — certified for aviation applications by System X.",
            },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-white/10 bg-card p-9" data-testid={`marine-type-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-bright/10 border border-bright/20 text-bright">
                  <c.icon size={22} />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold">{c.t}</h2>
                <p className="mt-3 text-sm text-steel leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="mt-14 rounded-2xl border border-bright/50 bg-gradient-to-b from-bright/10 to-card p-8 sm:p-12 blue-glow" data-testid="marine-phantom-card">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <span className="inline-block bg-bright text-white text-[11px] font-mono uppercase tracking-widest rounded-full px-3 py-1 mb-4">
                  The coating for marine & aviation
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">System X Phantom</h2>
                <p className="mt-3 max-w-2xl text-sm text-steel leading-relaxed">
                  Of the System X lineup, Phantom is the coating engineered for automotive, aviation and marine
                  applications — a hyper-durable ceramic shell with superior bonding where the environment is toughest.
                </p>
              </div>
              <img src="/geoshield-logo.svg" alt="GeoShield window film logo" className="hidden" loading="lazy" />
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                "Industry's first professional two-part ceramic system with superior bonding",
                "Dramatically stronger cross-linked matrix — outperforms traditional coatings",
                "Exceptional 9H hardness for superior scratch resistance",
                "Long-lasting UV protection that prevents fading",
                "Robust protection against corrosion and environmental damage",
                "Long-lasting gloss, on the water or in the hangar",
              ].map((f) => (
                <div key={f} className="flex items-start gap-3 text-sm text-steel">
                  <Check size={14} className="text-bright mt-0.5 shrink-0" /> {f}
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs font-mono text-bright">Lifetime warranty · Two-part ceramic system · Registered to your vehicle's Carfax</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
              <a
                href={BUSINESS.phoneHref}
                data-testid="marine-call-button"
                className="flex items-center justify-center gap-2 rounded-full bg-bright hover:bg-accent text-white font-semibold px-7 py-3.5 text-sm transition-colors"
              >
                Call {BUSINESS.phone}
              </a>
              <Link
                to="/book"
                data-testid="marine-quote-button"
                className="flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-bright hover:text-bright text-white font-semibold px-7 py-3.5 text-sm transition-colors"
              >
                Request a Free Quote
              </Link>
            </div>
          </div>
        </motion.div>

        <Reveal delay={0.15}>
          <p className="mt-6 text-xs text-muted">
            Marine & aviation coating is quoted per vessel and aircraft. For cars, trucks and daily drivers, see our{" "}
            <Link to="/ceramic-coatings" className="text-bright hover:text-white transition-colors">automotive ceramic coatings</Link>.
          </p>
        </Reveal>
      </section>

      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Eyebrow>Why Phantom for water & air</Eyebrow></Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Waves, t: "Built for harsh environments", d: "Robust defense against corrosion and environmental damage — where salt, water and sun never stop." },
              { icon: Ship, t: "One coating, three worlds", d: "The only System X coating certified across automotive, aviation and marine applications." },
              { icon: Plane, t: "Lifetime warranty", d: "Backed by a lifetime warranty — the strongest backing in the System X lineup." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-card p-7" data-testid={`marine-benefit-${i}`}>
                  <b.icon size={22} className="text-bright" />
                  <h3 className="mt-4 font-display text-lg font-bold">{b.t}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand testPrefix="marine" />
    </>
  );
}
